import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { MainContext } from '@blaze-cms/nextjs-components';
import { withTitle } from '../../HOC';
import Banner from '../Banner';
import Wrapper from '../Wrapper';
import CarouselWrapper from '../CarouselWrapper';
import Card from './Card';
import { getDynamicGridClasses } from './helpers';
import { getEntitiesWithBanner, parsePropsToDisplay } from '../../helpers';
import { getPropsToDisplayModifiers } from '../../utils';
import { checkIfShouldRenderBanner, checkNewBannerSettings } from '../List/components/helpers';

const CardContainer = ({
  cardData,
  gridModifier,
  style,
  displayCategory,
  displayThumbnail,
  displayTitle,
  modifier,
  entity: legacyEntity,
  graphqlEntityMap,
  propsToDisplay,
  gtmChildren,
  cardChildren,
  name,
  designConfig,
  itemsPerRow,
  enableSmoothScroll,
  enableCarousel,
  bannerModifier,
  priorityLimit,
  enableAutoScroll,
  enableOverlay,
  autoScrollTimer,
  overlayModifier,
  titleOverlayModifier,
  VariantComponent,
  ...cardProps
}) => {
  const { itemId } = useContext(MainContext);
  const { banner } = cardProps;
  // TODO: remove this check and all associated code once old banner setup is no longer supported
  const hasNewBannerSettings = checkNewBannerSettings(banner);
  const parsedCardsData = getEntitiesWithBanner(cardData, cardProps, hasNewBannerSettings);
  const propsToDisplayModifiers = getPropsToDisplayModifiers(propsToDisplay);
  const dynamicWrapperSizes = enableCarousel
    ? ''
    : getDynamicGridClasses('grid', itemsPerRow, designConfig);
  const InitialWrapper = enableCarousel ? CarouselWrapper : Wrapper;
  const CardWrapper = VariantComponent || InitialWrapper;
  let bannerIndex = 0;

  return (
    <CardWrapper
      className={dynamicWrapperSizes}
      modifiers={gridModifier}
      itemsPerRow={itemsPerRow}
      bannerModifier={bannerModifier}
      enableAutoScroll={enableAutoScroll}
      enableSmoothScroll={enableSmoothScroll}
      autoScrollTimer={autoScrollTimer}
      numberOfCards={parsedCardsData.length}>
      {parsedCardsData.map((currentEntity, index) => {
        const { baseAdunit, id, ...entityProps } = currentEntity;
        const shouldRenderBanner = !!banner && checkIfShouldRenderBanner(index, banner);
        const extraProps = parsePropsToDisplay(entityProps, propsToDisplay);
        const dynamicKey = [itemId, index].join('-');
        const priority = !!enableSmoothScroll || (priorityLimit > 0 && index + 1 <= priorityLimit);
        if (hasNewBannerSettings && shouldRenderBanner) bannerIndex += 1;
        if (!hasNewBannerSettings && baseAdunit) bannerIndex += 1;
        const parsedCardProps = {
          key: id,
          id,
          enableCarousel,
          graphqlEntityMap,
          propsToDisplay,
          parsedPropsToDisplay: extraProps,
          propsToDisplayModifiers,
          modifier,
          style,
          cardChildren,
          gtmChildren,
          gtmId: name,
          gridModifier,
          entityProps,
          displayCategory,
          displayThumbnail,
          displayTitle,
          priority,
          enableOverlay,
          overlayModifier,
          titleOverlayModifier,
          ...entityProps
        };
        const bannerProps = { ...banner, type: 'banner' };

        if (hasNewBannerSettings)
          return (
            <>
              <Card key={id} {...parsedCardProps} />
              {shouldRenderBanner && (
                <Banner
                  key={dynamicKey}
                  entity={graphqlEntityMap[entityProps.__typename]}
                  baseAdunit={baseAdunit}
                  {...entityProps}
                  {...bannerProps}
                  modifier={modifier}
                  renderCounter={bannerIndex}
                />
              )}
            </>
          );
        return baseAdunit ? (
          <Banner
            key={dynamicKey}
            entity={graphqlEntityMap[entityProps.__typename]}
            baseAdunit={baseAdunit}
            {...entityProps}
            {...bannerProps}
            modifier={modifier}
            renderCounter={bannerIndex}
          />
        ) : (
          <Card key={id} {...parsedCardProps} />
        );
      })}
    </CardWrapper>
  );
};

CardContainer.propTypes = {
  pageBuilderComponents: PropTypes.array,
  cardData: PropTypes.array.isRequired,
  graphqlEntityMap: PropTypes.object.isRequired,
  name: PropTypes.string,
  entity: PropTypes.string.isRequired,
  gridModifier: PropTypes.string,
  gtmChildren: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  cardChildren: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  propsToDisplay: PropTypes.array,
  displayCategory: PropTypes.bool,
  displayThumbnail: PropTypes.bool,
  displayTitle: PropTypes.bool,
  modifier: PropTypes.string,
  style: PropTypes.string,
  designConfig: PropTypes.object,
  itemsPerRow: PropTypes.number,
  enableSmoothScroll: PropTypes.bool,
  enableCarousel: PropTypes.bool,
  bannerModifier: PropTypes.string,
  priorityLimit: PropTypes.number,
  enableAutoScroll: PropTypes.bool,
  enableOverlay: PropTypes.bool,
  overlayModifier: PropTypes.string,
  titleOverlayModifier: PropTypes.string,
  autoScrollTimer: PropTypes.number,
  VariantComponent: PropTypes.func
};

CardContainer.defaultProps = {
  gtmChildren: [],
  cardChildren: [],
  pageBuilderComponents: [],
  name: '',
  gridModifier: '',
  propsToDisplay: [],
  displayCategory: true,
  displayThumbnail: true,
  displayTitle: true,
  modifier: '',
  style: 'portrait',
  designConfig: {},
  itemsPerRow: 1,
  enableSmoothScroll: false,
  enableCarousel: false,
  bannerModifier: '',
  priorityLimit: 0,
  enableAutoScroll: false,
  enableOverlay: false,
  autoScrollTimer: 0,
  overlayModifier: '',
  titleOverlayModifier: '',
  VariantComponent: null
};

export default withTitle(CardContainer);
